import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { DesktopPagination } from '../common';
import { fetchProjectUsage } from './ocrApi';

/**
 * LLM 사용량 카드 (FE-3) — 프로젝트 상세 개요 탭.
 *
 * 총계(호출·토큰·비용) + 작업별(by_work_type) 상시 표시, 페이지별(by_page)은 접이식(+페이지네이션).
 * total/by_work_type은 전체 총계이고 페이지네이션은 by_page에만 적용된다(ocrApi 계약).
 */

const PER_PAGE = 20;

const fmtInt = (v) => (typeof v === 'number' ? v.toLocaleString() : '—');
const fmtCost = (v) => (typeof v === 'number' ? `$${v.toFixed(3)}` : '—');

function OcrUsageCard({ projectId }) {
  const { getAccessToken } = useAuth();

  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pagesOpen, setPagesOpen] = useState(false);
  const reqSeqRef = useRef(0); // 페이지 빠른 전환 시 이전 응답이 최신 값을 덮지 않도록 최신 요청만 반영

  const load = useCallback(async (pageNum = 1) => {
    const seq = (reqSeqRef.current += 1);
    setLoading(true);
    setLoadError(null);
    try {
      const d = await fetchProjectUsage(projectId, { page: pageNum, perPage: PER_PAGE }, getAccessToken);
      if (seq !== reqSeqRef.current) return; // stale 응답 무시
      setUsage(d);
    } catch (e) {
      if (seq !== reqSeqRef.current) return;
      setUsage(null);
      setLoadError(e.message || 'LLM 사용량을 불러오지 못했습니다.');
    } finally {
      if (seq === reqSeqRef.current) setLoading(false);
    }
  }, [projectId, getAccessToken]);

  useEffect(() => {
    load(1);
  }, [load]);

  const total = usage?.total || {};
  const byWorkType = Array.isArray(usage?.by_work_type) ? usage.by_work_type : [];
  const byPage = Array.isArray(usage?.by_page) ? usage.by_page : [];
  const totalPages = usage?.total_pages ?? 0;
  const page = usage?.page ?? 1;
  const totalItems = usage?.total_items ?? byPage.length;

  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-lg">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-900">LLM 사용량</h3>
        <button
          type="button"
          onClick={() => load(1)}
          disabled={loading}
          className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          aria-label="사용량 새로고침"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && !usage ? (
        <div className="py-4 text-center text-sm text-gray-500">로딩 중...</div>
      ) : loadError ? (
        <div className="py-2 text-sm text-gray-500">{loadError}</div>
      ) : (
        <>
          {/* 총계 */}
          <div className="text-sm text-gray-700">
            호출 <span className="font-bold text-gray-900">{fmtInt(total.calls)}</span>회
            <span className="mx-1.5 text-gray-300">|</span>
            토큰 입력 <span className="font-bold text-gray-900">{fmtInt(total.tokens_in)}</span>
            {' · '}출력 <span className="font-bold text-gray-900">{fmtInt(total.tokens_out)}</span>
            <span className="mx-1.5 text-gray-300">|</span>
            비용 <span className="font-bold text-gray-900">{fmtCost(total.cost_usd)}</span>
          </div>

          {/* 작업별 */}
          {byWorkType.length > 0 && (
            <div className="mt-2 space-y-1">
              {byWorkType.map((w) => (
                <div key={w.work_type} className="flex items-center justify-between text-xs text-gray-600">
                  <span>{w.label || `작업 ${w.work_type}`}</span>
                  <span>
                    {fmtInt(w.calls)}회 · {fmtInt(w.tokens_in)}→{fmtInt(w.tokens_out)} · {fmtCost(w.cost_usd)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* 페이지별 (접이식) */}
          <button
            type="button"
            onClick={() => setPagesOpen((v) => !v)}
            className="mt-2 flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            {pagesOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            페이지별 내역 ({fmtInt(totalItems)}건)
          </button>
          {pagesOpen && (
            <div className="mt-2">
              {byPage.length > 0 ? (
                <div className="overflow-x-auto rounded-xl border">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wider text-gray-500">페이지</th>
                        <th className="px-2 py-1.5 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500">모델</th>
                        <th className="px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wider text-gray-500">호출</th>
                        <th className="px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wider text-gray-500">토큰(입력→출력)</th>
                        <th className="px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wider text-gray-500">비용</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {byPage.map((row) => (
                        <tr key={row.page_id} className="border-b border-gray-100 last:border-b-0">
                          <td className="whitespace-nowrap px-2 py-1.5 text-center text-xs text-gray-900">
                            p.{(row.page_index ?? 0) + 1}
                          </td>
                          <td className="max-w-[160px] truncate px-2 py-1.5 text-xs text-gray-600" title={row.model}>
                            {row.model || '—'}
                          </td>
                          <td className="whitespace-nowrap px-2 py-1.5 text-center text-xs text-gray-600">{fmtInt(row.calls)}</td>
                          <td className="whitespace-nowrap px-2 py-1.5 text-center text-xs text-gray-600">
                            {fmtInt(row.tokens_in)}→{fmtInt(row.tokens_out)}
                          </td>
                          <td className="whitespace-nowrap px-2 py-1.5 text-center text-xs text-gray-600">{fmtCost(row.cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-2 text-center text-xs text-gray-400">페이지별 내역이 없습니다.</div>
              )}
              {totalPages > 1 && (
                <div className="mt-2">
                  <DesktopPagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={(newPage) => load(newPage)}
                    itemsPerPage={PER_PAGE}
                    totalItems={totalItems}
                    showInfo={false}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default OcrUsageCard;
